
import { Component, Provide, ProvideReactive, Vue, Watch } from 'vue-property-decorator';
import RegistroPontoMain from './components/RegistroPontoMain.vue';
import RegistroPontoQrCode from './components/RegistroPontoQrCode.vue';
import RegistroPontoRecFacial from './components/RegistroPontoRecFacial.vue';
import RegistroPontoController from '@/assets/ts/controller/RegistroPontoController';
import RegistroPontoConfirmationAlmoco from './components/RegistroPontoConfirmationAlmoco.vue';
import RegistroPontoPhoto from './components/RegistroPontoPhoto.vue';
import RegistroPontoSave from './components/RegistoPontoSave.vue';
import { CacheMarcacoes, LocalizacaoPontoUnidade, Tabs, VinculoPontoUnidade } from './types-registro-ponto';
import SnackbarUtils from '@tekvue/assets/utils/SnackbarUtils';
import CancelError from './CancelError';
import Constants from '@/assets/ts/configuration/Constants';
import { Mutation } from 'vuex-class';
import OfflineController from '@/assets/ts/controller/OfflineController';
import OperadorController from '@/assets/ts/controller/OperadorController';
import LocalizacaoController from '@/assets/ts/controller/LocalizacaoController';
import DynamicRepository from '@/assets/ts/offline/repository/DynamicRepository';
import { OfflineTablesName } from '@/assets/ts/offline/DynamicTables';
import registroPontoMessages from '@/assets/ts/messages/registroPontoMessages';
import axios from 'axios';
import Time from '@tekvue/assets/utils/Time';

@Component({
  components: {
    RegistroPontoMain,
    RegistroPontoQrCode,
    RegistroPontoRecFacial,
    RegistroPontoConfirmationAlmoco,
    RegistroPontoPhoto,
    RegistroPontoSave,
  },
})
export default class RegistroPontoNovo extends Vue {
  //#region [ CONSTANTS ]
  readonly Tabs = Tabs;
  readonly TIMEOUT_CANCEL_REQUEST = 10_000;
  //#endregion



  //#region [ REFS ]
  get registroPontoPhoto(): RegistroPontoPhoto {
    return this.$refs.registroPontoPhoto as RegistroPontoPhoto || null;
  }

  get registroPontoConfirmationAlmoco(): RegistroPontoConfirmationAlmoco {
    return this.$refs.registroPontoConfirmationAlmoco as RegistroPontoConfirmationAlmoco || null;
  }

  get registroPontoQrCode(): RegistroPontoQrCode {
    return this.$refs.registroPontoQrCode as RegistroPontoQrCode || null;
  }

  get registroPontoRecFacial(): RegistroPontoRecFacial {
    return this.$refs.registroPontoRecFacial as RegistroPontoRecFacial || null;
  }

  get registroPontoSave(): RegistroPontoSave {
    return this.$refs.registroPontoSave as RegistroPontoSave || null;
  }

  get registroPontoMain(): RegistroPontoMain {
    return this.$refs.registroPontoMain as RegistroPontoMain || null;
  }
  //#endregion



  //#region [ STORE ]
  get estruturaGerencialAtual(): any {
    return this.$store.getters.estruturaGerencialAtual;
  }

  get photoIsRequired(): boolean {
    return this.$store.getters.user.idexigefotoregpntpg;
  }

  @Mutation('setIntervalRPUReloadId')
  setIntervalRPUReloadId: any;
  //#endregion



  //#region [ DATA ]
  tab: Tabs = 0;
  vinculos: VinculoPontoUnidade[] = [];
  @Provide() findVinculo = this._findVinculo;
  //#endregion



  //#region [ COMPUTED ]
  get vinculosMappedByCPF(): Map<string, VinculoPontoUnidade> {
    const map = new Map<string, VinculoPontoUnidade>();

    this.vinculos.forEach(vinculo => {
      if (vinculo.NRCPFPESSOA) {
        map.set(vinculo.NRCPFPESSOA, vinculo);
      }
    });

    return map;
  }

  get vinculosMappedByNrVinculo(): Map<string, VinculoPontoUnidade> {
    const map = new Map<string, VinculoPontoUnidade>();

    this.vinculos.forEach(vinculo => {
      if (vinculo.NRVINCULOM) {
        map.set(vinculo.NRVINCULOM.toString(), vinculo);
      }
    });

    return map;
  }

  @ProvideReactive('vinculos')
  get _vinculos(): VinculoPontoUnidade[] {
    return this.vinculos;
  }

  private get isMobile() {
    return OfflineController.isMobile();
  }
  //#endregion



  //#region [ LIFECYCLE ]
  created(): void {
    this.loadVinculos();
    this.setIntervalReload();

    if(!OfflineController.checkIsOffline()) {
      OperadorController.restringeApenasMarcacaoPG();
    }
  }
  //#endregion



  //#region [ WATCHERS ]
  @Watch('estruturaGerencialAtual')
  private estruturaGerencialAtualOnChange(): void {
    this.loadVinculos();
  }
  //#endregion



  //#region [ METHODS ]
  goToTab(tab: Tabs): void {
    this.tab = tab;
  }

  _findVinculo(id: string): VinculoPontoUnidade|null {
    return this.vinculosMappedByCPF.get(id) || this.vinculosMappedByNrVinculo.get(id) || null;
  }

  async loadVinculos(): Promise<void> {
    let vinculos: VinculoPontoUnidade[] = [];
    let timeoutCancel: number = null;

    if (this.isMobile) {
        // Caso existe vínculos em cache, um timeout de 10 segundos é configurado para cancelar 
        // a requisição caso ela ultrapasse esse tempo limite, permitindo que o fluxo continue no modo offline.

        vinculos = await DynamicRepository.getDynamicTable(OfflineTablesName.GPE_VINCULOM);
        if (vinculos && vinculos.length) {
          timeoutCancel = this.TIMEOUT_CANCEL_REQUEST;
        }
      }

    try {
      vinculos = await RegistroPontoController.getVinculosDoGestorAndPerfilRegPntMlt(true, timeoutCancel);
    } catch (err) {
      if (vinculos.length > 0) {
        SnackbarUtils.showMessage(registroPontoMessages.vinculosGestorCache, SnackbarUtils.TYPE_ALERT);
      } else {
        SnackbarUtils.showMessage(registroPontoMessages.vinculosGestorFailedToLoad, SnackbarUtils.TYPE_ERROR);
      }
    } finally {
      this.vinculos = vinculos;
    }
  }

  async onPontoRequested(vinculo: VinculoPontoUnidade, almoco: boolean, photo?: string): Promise<void> {
    if (this.photoIsRequired && !photo) {
      SnackbarUtils.showMessage(vinculo.NRNMVINCULO, SnackbarUtils.TYPE_INFO2, 3000);
      photo = await this.takePhoto();
    }

    this.save(vinculo, almoco, photo);
  }

  async save(vinculo: VinculoPontoUnidade, almoco: boolean, photo: string = null): Promise<void> {
    this.goToTab(Tabs.SAVE);
    this.registroPontoSave.startLoading(vinculo);
    const localizacao = await LocalizacaoController.getLocalizacaoRegistroPonto();

    try {
      const data = await RegistroPontoController.enviaMarcacao(almoco, vinculo, photo, localizacao, false);
      this.registroPontoSave.success();
      this.registroPontoMain.clearPin();
      vinculo.BATIDAS_DIA_ATUAL = (data.marcacoesDia || []).join(',');
    } catch (error) {
      let errorMessage = 'Erro ao salvar o ponto.';

      if (error instanceof Error) errorMessage = error.message;
      if (typeof error === 'string') errorMessage = error;

      this.registroPontoSave.error(errorMessage);

      if (this.isMobile) {
        await this.saveOffline(vinculo, almoco, photo, localizacao);
        if (axios.isCancel(error)) {
          OfflineController.setOfflineManually();
        }
        return;
      }
    }
  }

  async saveOffline(vinculo: VinculoPontoUnidade, almoco: boolean, photo: string, localizacao: LocalizacaoPontoUnidade): Promise<void> {
    const result = await RegistroPontoController.enviaMarcacaoOffline(almoco, vinculo, photo, localizacao, false);

    if (result) {
      SnackbarUtils.showMessage(registroPontoMessages.registerSavedOffline, SnackbarUtils.TYPE_SUCCESS);
    } else {
      SnackbarUtils.showMessage(registroPontoMessages.registerNotSavedOffline, SnackbarUtils.TYPE_ALERT);
    }
  }

  /** Verifica se a próxima batida de ponto será de saída. Se o número de marcações atual for ímpar, quer dizer
   * que a última marcação foi de entrada, logo a próxima será de saída.
   */
  isLeaving(vinculo: VinculoPontoUnidade): boolean {
    if (!vinculo.BATIDAS_DIA_ATUAL) {
      return false;
    }

    const oddMarcacoes = vinculo.BATIDAS_DIA_ATUAL.split(',').length % 2 !== 0;
    return oddMarcacoes;
  }

  async takePhoto(): Promise<string|null> {
    this.goToTab(Tabs.PHOTO);
    try {
      return await this.registroPontoPhoto.takePhoto();
    } catch (err) {
      SnackbarUtils.showMessage(registroPontoMessages.cameraAccessError, SnackbarUtils.TYPE_ERROR);
      this.goToTab(Tabs.MAIN);
      throw err;
    }
  }

  async askAlmoco(vinculo: VinculoPontoUnidade): Promise<boolean> {
    this.goToTab(Tabs.ALMOCO);
    return await this.registroPontoConfirmationAlmoco.ask(vinculo);
  }

  async openQrCodeScanner(): Promise<void> {
    try {
      this.goToTab(Tabs.QR_CODE);
      const vinculo = await this.registroPontoQrCode.scan();
      this.onVinculoIdentified(vinculo);
    } catch (error) {
      this.goToTab(Tabs.MAIN);

      if (!(error instanceof CancelError)) {
        this.showExceptionMessage(error);
        throw error;
      }
    }
  }

  async openRecFacial(): Promise<void> {
    const estruturaHasHash = this.vinculos.some((vinculo) => vinculo.HASHFACIAL);
    if (!estruturaHasHash) {
      SnackbarUtils.showMessage(registroPontoMessages.noHashEstrutura, SnackbarUtils.TYPE_ERROR);
      return;
    }

    try {
      this.goToTab(Tabs.R_FACIAL);
      const [vinculo, photo] = await this.registroPontoRecFacial.scan();
      this.onVinculoIdentified(vinculo, photo);
    } catch (error) {
      this.goToTab(Tabs.MAIN);

      if (!(error instanceof CancelError)) {
        this.showExceptionMessage(error);
        throw error;
      }
    }
  }

  async onVinculoIdentified(vinculo: VinculoPontoUnidade, photo?: string): Promise<void> {
    let almoco = false;

    if (vinculo.ISVINCULOFERIAS === 'S') {
      SnackbarUtils.showMessage(registroPontoMessages.vinculoDeFerias, SnackbarUtils.TYPE_ALERT);
      this.goToTab(Tabs.MAIN);
      return;
    }

    if (this.isMarcacaoMesmoMinuto(vinculo)) {
      SnackbarUtils.showMessage(registroPontoMessages.sameMinuteRegister, SnackbarUtils.TYPE_ALERT);
      this.goToTab(Tabs.MAIN);
      return;
    }

    try {
      if (this.isLeaving(vinculo)) {
        almoco = await this.askAlmoco(vinculo);
      }

      this.onPontoRequested(vinculo, almoco, photo);
    } catch (error) {
      if (!(error instanceof CancelError)) {
        let errorMessage = 'Erro ao identificar o vínculo.';

        if (error instanceof Error) errorMessage = error.message;
        if (typeof error === 'string') errorMessage = error;

        SnackbarUtils.showMessage(errorMessage, SnackbarUtils.TYPE_ERROR, 3000);

        throw error;
      }
    }
  }

  setIntervalReload(): void {
    const intervalId = setInterval(this.loadVinculos, 1000 * 60 * Constants.EMS_MINUTES_RELOAD_INTERVAL);
    this.setIntervalRPUReloadId(intervalId);
  }

  showExceptionMessage(err: unknown): void {
    let errorMessage = 'Erro!';

    if (err instanceof Error) errorMessage = err.message;
    if (typeof err === 'string') errorMessage = err;

    SnackbarUtils.showMessage(errorMessage, SnackbarUtils.TYPE_ERROR);
  }

  isMarcacaoMesmoMinuto(vinculo: VinculoPontoUnidade) {
    const marcacoes = this.getMarcacoesVinculo(vinculo);
    const ultimoHorario = marcacoes[marcacoes.length - 1];

    if (!ultimoHorario) {
      return false;
    }

    const difference = Time.fromCurrentTime().getSeconds() - Time.fromString(ultimoHorario).getSeconds();
    const verificacao =  difference> 0  && difference < 180;

    return verificacao;
  }

  getMarcacoesVinculo(vinculo: VinculoPontoUnidade): string[] {
    return (vinculo.BATIDAS_DIA_ATUAL || '').split(',');
  }
  //#endregion
}
